import React from 'react';
import styled from 'styled-components';
import { useLanguage } from '../contexts/LanguageContext';
import { useScrollZoom } from '../hooks/useScrollZoom';

const ContactSection = styled.section`
  padding: 3rem 0 2rem;
  background: linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-primary) 100%);
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 2.5rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  font-weight: 600;
  color: var(--brand-primary);
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    font-size: 1.8rem;
    font-family: 'Playfair Display', serif;
  }

  @media (max-width: 480px) {
    font-size: 1.6rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1rem;
  color: var(--text-secondary);
  max-width: 500px;
  margin: 0 auto;
`;

const ContactContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const ContactInfo = styled.div`
  background: var(--bg-elevated);
  padding: 1.5rem;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.2rem;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0.8rem;
  border-radius: 10px;

  &:hover {
    background: var(--bg-secondary);
    transform: translateX(5px);
  }

  &:last-child {
    margin-bottom: 0;
  }

  i {
    color: var(--brand-primary);
    font-size: 1.2rem;
    width: 20px;
    text-align: center;
  }
`;

const ContactItemContent = styled.div`
  flex: 1;
`;

const ContactItemTitle = styled.h4`
  color: var(--brand-primary);
  margin-bottom: 0.2rem;
  font-size: 1rem;
`;

const ContactItemText = styled.p`
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin: 0;
  word-break: break-all;
`;

const SocialLinksSection = styled.div`
  background: var(--bg-elevated);
  padding: 1.5rem;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
  text-align: center;
`;

const SocialTitle = styled.h3`
  color: var(--brand-primary);
  margin-bottom: 1rem;
  font-size: 1.1rem;
`;

const SocialButtons = styled.div`
  display: flex;
  gap: 0.8rem;
  justify-content: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.6rem;
    padding: 0 0.5rem;
  }
`;

const SocialBtn = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0.7rem 1.2rem;
  text-decoration: none;
  color: white;
  border-radius: 12px;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.15);
  font-size: 0.9rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.25);
  }

  &.facebook {
    background: linear-gradient(135deg, #1877F2, #42A5F5);
  }

  &.instagram {
    background: linear-gradient(135deg, #E4405F, #833AB4, #FD1D1D, #FCB045);
  }

  &.tiktok {
    background: linear-gradient(135deg, #FF0050, #000000);
  }

  i {
    font-size: 1rem;
    transition: transform 0.3s ease;
  }

  &:hover i {
    transform: scale(1.2);
  }

  @media (max-width: 768px) {
    justify-content: center;
    padding: 0.85rem 1.2rem;
    border-radius: 12px;
    font-size: 0.95rem;

    i {
      font-size: 1.1rem;
      width: 20px;
      text-align: center;
    }
  }
`;

const ZaloQRContainer = styled.div`
  margin-top: 1.2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
`;

const ZaloQRLabel = styled.p`
  color: var(--text-secondary);
  font-size: 0.85rem;
  margin: 0;
`;

const ZaloQRImage = styled.img`
  width: 150px;
  height: 150px;
  border-radius: 10px;
  object-fit: contain;

  @media (max-width: 480px) {
    width: 120px;
    height: 120px;
  }
`;

const MapSection = styled.div`
  margin-top: 2rem;
  background: var(--bg-elevated);
  padding: 1.5rem;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
`;

const MapTitle = styled.h3`
  color: var(--brand-primary);
  margin-bottom: 1rem;
  font-size: 1.1rem;
  text-align: center;
`;

const MapContainer = styled.div`
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  
  iframe {
    width: 100%;
    height: 350px;
    border: 0;
    
    @media (max-width: 768px) {
      height: 280px;
    }
    
    @media (max-width: 480px) {
      height: 250px;
    }
  }
`;

const Contact = () => {
  const { t } = useLanguage();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const scrollZoom = useScrollZoom({ disabled: reducedMotion });

  const handleContactClick = (type) => {
    switch (type) {
      case 'phone':
        window.location.href = 'tel:+84888641198';
        break;
      case 'email':
        window.location.href = 'mailto:hoanguyendalathomestay@gmail.com';
        break;
      case 'location':
        window.open('https://maps.app.goo.gl/e3C8mUdJGBHZ2aDB6', '_blank');
        break;
      default:
        break;
    }
  };

  return (
    <ContactSection id="contact">
      <Container>
        <SectionHeader data-aos="fade-up" ref={scrollZoom.ref} style={scrollZoom.style}>
          <SectionTitle>
            {t("Get in Touch", "Liên hệ với chúng tôi")}
          </SectionTitle>
          <SectionSubtitle>
            {t("Ready to book your stay? Let's connect!", "Sẵn sàng đặt phòng? Hãy kết nối với chúng tôi!")}
          </SectionSubtitle>
        </SectionHeader>
        
        <ContactContent>
          <ContactInfo data-aos="fade-right" data-aos-delay="100">
            <ContactItem onClick={() => handleContactClick('location')}>
              <i className="fas fa-map-marker-alt"></i>
              <ContactItemContent>
                <ContactItemTitle>
                  {t("Location", "Địa chỉ")}
                </ContactItemTitle>
                <ContactItemText>
                  {t("Hoàng Uyên Đà Lạt Homestay, Da Lat City, Lam Dong Province, Vietnam", "Hoàng Uyên Đà Lạt Homestay, Thành phố Đà Lạt, Tỉnh Lâm Đồng, Việt Nam")}
                </ContactItemText>
              </ContactItemContent>
            </ContactItem>
            
            <ContactItem onClick={() => handleContactClick('phone')}>
              <i className="fas fa-phone"></i>
              <ContactItemContent>
                <ContactItemTitle>
                  {t("Phone", "Điện thoại")}
                </ContactItemTitle>
                <ContactItemText>+84 888 641 198</ContactItemText>
              </ContactItemContent>
            </ContactItem>
            
            <ContactItem onClick={() => handleContactClick('email')}>
              <i className="fas fa-envelope"></i>
              <ContactItemContent>
                <ContactItemTitle>Email</ContactItemTitle>
                <ContactItemText>hoanguyendalathomestay@gmail.com</ContactItemText>
              </ContactItemContent>
            </ContactItem>
          </ContactInfo>
          
          <SocialLinksSection data-aos="fade-left" data-aos-delay="200">
            <SocialTitle>
              {t("Follow Us", "Theo dõi chúng tôi")}
            </SocialTitle>
            <SocialButtons>
              <SocialBtn 
                href="https://www.facebook.com/HoangUyenDaLatHomestay" 
                className="facebook" 
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fab fa-facebook-f"></i>
                <span>Facebook</span>
              </SocialBtn>
              <SocialBtn 
                href="https://www.instagram.com/hoanguyendalathomestay/" 
                className="instagram" 
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fab fa-instagram"></i>
                <span>Instagram</span>
              </SocialBtn>
              <SocialBtn 
                href="https://www.tiktok.com/@hoanguyendalat" 
                className="tiktok" 
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fab fa-tiktok"></i>
                <span>TikTok</span>
              </SocialBtn>
            </SocialButtons>
            <ZaloQRContainer>
              <ZaloQRLabel>{t("Scan to follow us on Zalo", "Quét mã để theo dõi Zalo")}</ZaloQRLabel>
              <ZaloQRImage src="/zalo_qr.png" alt={t("Zalo QR Code", "Mã QR Zalo")} />
            </ZaloQRContainer>
          </SocialLinksSection>
        </ContactContent>
        
        <MapSection data-aos="fade-up" data-aos-delay="100">
          <MapTitle>
            {t("Find Us on Map", "Tìm chúng tôi trên bản đồ")}
          </MapTitle>
          <MapContainer>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3903.5362150774395!2d108.42118477585505!3d11.937330388290995!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31716d22ad81d587%3A0x8525c2fb5352b498!2zSG_DoG5nIFV5w6puIMSQw6AgTOG6oXQgSG9tZXN0YXk!5e0!3m2!1svi!2ssg!4v1760452423562!5m2!1svi!2ssg"
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title={t("Hoang Uyen Da Lat Homestay Location", "Vị trí Hoàng Uyên Đà Lạt Homestay")}
            />
          </MapContainer>
        </MapSection>

      </Container>
    </ContactSection>
  );
};

export default Contact;